import { Profile, EntrepreneurProfile, MentorProfile } from "@/hooks/useProfile";
import { ProfilePrivate } from "@/hooks/useProfilePrivate";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Lock, ShieldCheck, FileText, ExternalLink, Loader2 } from "lucide-react";
import { MyBadgesCard } from "@/components/badges/MyBadgesCard";
import { CertificatesSection } from "./CertificatesSection";
import { EmailPreferencesSection } from "./EmailPreferencesSection";
import { MentorTopicsSection } from "./MentorTopicsSection";
import { MentorAvailabilityToggle } from "./MentorAvailabilityToggle";
import { MentorContactPrivacySection } from "./MentorContactPrivacySection";
import { Button } from "@/components/ui/button";
import { getEducationLabel } from "@/lib/educationLevels";
import { getCompanyStageLabel } from "@/lib/companyStages";
import { useStorageUrl } from "@/hooks/useStorageUrl";
import { useSignedStorageUrl } from "@/hooks/useSignedStorageUrl";

const PHONE_TYPES: Record<string, string> = {
  mobile: "Mobile",
  home: "Home",
  work: "Work",
};

const REFERRED_BY_OPTIONS: Record<string, string> = {
  food_for_the_poor: "Food For The Poor",
  emprezz_golding: "Emprezz Golding",
  talk_up_yout: "Talk Up Yout",
  word_of_mouth: "Word of Mouth",
  social_media: "Social Media",
  website: "Website",
  other: "Other",
};

const GENDER_OPTIONS: Record<string, string> = {
  female: "Female",
  male: "Male",
  other: "Other",
  prefer_not_to_say: "Prefer Not to Say",
};

const AGE_RANGE_OPTIONS: Record<string, string> = {
  "12-17": "12-17",
  "18-30": "18-30",
  "31-45": "31-45",
  "45+": "45+",
};

const HAS_DEVICE_OPTIONS: Record<string, string> = {
  yes: "Yes",
  no: "No",
  maybe: "Maybe",
};

// Using centralized education level labels from educationLevels.ts

const JAMAICAN_PARISHES: Record<string, string> = {
  kingston: "Kingston",
  st_andrew: "St. Andrew",
  st_thomas: "St. Thomas",
  portland: "Portland",
  st_mary: "St. Mary",
  st_ann: "St. Ann",
  trelawny: "Trelawny",
  st_james: "St. James",
  hanover: "Hanover",
  westmoreland: "Westmoreland",
  st_elizabeth: "St. Elizabeth",
  manchester: "Manchester",
  clarendon: "Clarendon",
  st_catherine: "St. Catherine",
};

const COUNTRY_OPTIONS: Record<string, string> = {
  jamaica: "Jamaica",
  united_states: "United States",
};

interface MentorProfileExtended {
  id: string;
  professional_background: string | null;
  years_experience: number | null;
  career_highlights: string | null;
  availability_notes: string | null;
  is_available_for_mentorship?: boolean;
}

interface ProfileViewModeProps {
  profile: Profile;
  profilePrivate: ProfilePrivate | null;
  entrepreneurProfile: EntrepreneurProfile | null;
  mentorProfile: MentorProfileExtended | null;
  userSkills: Array<{ skill_id: string; skills: { id: string; name: string; category: string } | null }>;
  userIndustries: Array<{ industry_id: string; industries: { id: string; name: string } | null }>;
}

function DisplayField({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="space-y-1">
      <p className="text-sm font-medium text-muted-foreground">{label}</p>
      <p className="text-sm">{value || "—"}</p>
    </div>
  );
}

export function ProfileViewMode({
  profile,
  profilePrivate,
  entrepreneurProfile,
  mentorProfile,
  userSkills,
  userIndustries,
}: ProfileViewModeProps) {
  const isEntrepreneur = profile.role === "entrepreneur";
  const avatarUrl = useStorageUrl("avatars", profile.avatar_url);
  const { url: pitchDeckUrl, loading: pitchDeckLoading } = useSignedStorageUrl("pitch-decks", profile.pitch_deck_url);

  const formatPhone = () => {
    if (!profilePrivate?.phone) return "—";
    const phoneType = profilePrivate.phone_type ? ` (${PHONE_TYPES[profilePrivate.phone_type] || profilePrivate.phone_type})` : "";
    const whatsapp = profilePrivate.is_whatsapp ? " • WhatsApp" : "";
    return `${profilePrivate.phone}${phoneType}${whatsapp}`;
  };

  const formatLocation = () => {
    const parts: string[] = [];
    if (profilePrivate?.parish) {
      parts.push(JAMAICAN_PARISHES[profilePrivate.parish] || profilePrivate.parish);
    }
    if (profilePrivate?.country) {
      parts.push(COUNTRY_OPTIONS[profilePrivate.country] || profilePrivate.country);
    }
    return parts.length > 0 ? parts.join(", ") : "—";
  };

  return (
    <div className="space-y-6">
      {/* Basic Info */}
      <Card>
        <CardHeader>
          <CardTitle>Basic Information</CardTitle>
          <CardDescription>Your public profile information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <Avatar className="h-20 w-20">
              <AvatarImage src={avatarUrl || undefined} />
              <AvatarFallback className="bg-primary text-primary-foreground text-2xl">
                {profile.full_name?.charAt(0) || "?"}
              </AvatarFallback>
            </Avatar>
            <div className="space-y-2">
              <p className="text-lg font-semibold">{profile.full_name}</p>
              <div className="flex items-center gap-2">
                <Badge variant={isEntrepreneur ? "default" : "secondary"} className={isEntrepreneur ? "badge-entrepreneur" : "badge-mentor"}>
                  {profile.role}
                </Badge>
                {!profile.is_published && (
                  <Badge variant="outline" className="text-warning border-warning">
                    Pending Approval
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <DisplayField label="Email" value={profilePrivate?.email} />
            <DisplayField label="Location" value={formatLocation()} />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <DisplayField label="Phone" value={formatPhone()} />
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">LinkedIn</p>
              {profilePrivate?.linkedin_url === "DOES_NOT_HAVE_LINKEDIN" ? (
                <p className="text-sm text-muted-foreground italic">Does Not Have LinkedIn Profile</p>
              ) : profilePrivate?.linkedin_url ? (
                <a 
                  href={profilePrivate.linkedin_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline flex items-center gap-1"
                >
                  View Profile <ExternalLink className="h-3 w-3" />
                </a>
              ) : (
                <p className="text-sm">—</p>
              )}
            </div>
          </div>

          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">Bio</p>
            <p className="text-sm whitespace-pre-wrap">{profile.bio || "—"}</p>
          </div>
        </CardContent>
      </Card>

      {/* Badges Section */}
      <MyBadgesCard userId={profile.id} />

      {/* Certificates Section */}
      <CertificatesSection />

      {/* Private Demographic Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            Demographic Information
          </CardTitle>
          <CardDescription>
            <Alert className="mt-2 border-primary/20 bg-primary/5">
              <Lock className="h-4 w-4" />
              <AlertDescription>
                This information will never be made public. It is only used by Entrepreneurs Across Borders for internal record keeping and research purposes.
              </AlertDescription>
            </Alert>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <DisplayField 
              label="Referred By" 
              value={profile.referred_by ? REFERRED_BY_OPTIONS[profile.referred_by] || profile.referred_by : null} 
            />
            <DisplayField 
              label="Gender" 
              value={profile.gender ? GENDER_OPTIONS[profile.gender] || profile.gender : null} 
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <DisplayField 
              label="Age Range" 
              value={profile.age_range ? AGE_RANGE_OPTIONS[profile.age_range] || profile.age_range : null} 
            />
            <DisplayField 
              label="Device Access" 
              value={profile.has_device ? HAS_DEVICE_OPTIONS[profile.has_device] || profile.has_device : null} 
            />
          </div>
          <DisplayField 
            label="Education Level" 
            value={profile.education_level ? getEducationLabel(profile.education_level) : null} 
          />
        </CardContent>
      </Card>

      {/* Role-specific Info */}
      {isEntrepreneur ? (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Company Information</CardTitle>
              <CardDescription>Details about your startup or business</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <DisplayField label="Company/Startup Name" value={entrepreneurProfile?.company_name} />
                <DisplayField 
                  label="Company Stage" 
                  value={entrepreneurProfile?.startup_stage ? getCompanyStageLabel(entrepreneurProfile.startup_stage) : null} 
                />
              </div>
              <DisplayField label="Company Description" value={entrepreneurProfile?.company_description} />
              <div className="grid gap-4 md:grid-cols-2">
                <DisplayField label="Target Market" value={entrepreneurProfile?.target_market} />
                <DisplayField label="Funding Status" value={entrepreneurProfile?.funding_status} />
              </div>
              <DisplayField label="Specific Challenges/Needs" value={entrepreneurProfile?.specific_challenges} />
            </CardContent>
          </Card>

          {/* Pitch Deck View */}
          {profile.pitch_deck_url && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Pitch Deck
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Button 
                  variant="outline" 
                  disabled={pitchDeckLoading || !pitchDeckUrl}
                  onClick={() => pitchDeckUrl && window.open(pitchDeckUrl, '_blank')}
                >
                  {pitchDeckLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <FileText className="mr-2 h-4 w-4" />
                  )}
                  View Pitch Deck
                </Button>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <>
          {/* Mentor Availability Toggle - Only for mentors viewing their own profile */}
          <MentorAvailabilityToggle 
            isAvailable={mentorProfile?.is_available_for_mentorship ?? true}
            isLoading={!mentorProfile}
          />

          <Card>
            <CardHeader>
              <CardTitle>Professional Background</CardTitle>
              <CardDescription>Your expertise and experience</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <DisplayField label="Professional Summary" value={mentorProfile?.professional_background} />
              <div className="grid gap-4 md:grid-cols-2">
                <DisplayField 
                  label="Years of Experience" 
                  value={mentorProfile?.years_experience?.toString()} 
                />
                <DisplayField label="Availability" value={mentorProfile?.availability_notes} />
              </div>
              <DisplayField label="Career Highlights" value={mentorProfile?.career_highlights} />
            </CardContent>
          </Card>

          {/* Mentor Topics Section - Only for mentors */}
          <MentorTopicsSection />

          {/* Contact Privacy Section - Only for mentors */}
          <MentorContactPrivacySection isEditMode={false} />
        </>
      )}

      {/* Industries */}
      <Card>
        <CardHeader>
          <CardTitle>Industries</CardTitle>
          <CardDescription>
            {isEntrepreneur ? "Your industry/vertical" : "Industries you have experience in"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {userIndustries && userIndustries.length > 0 ? (
              userIndustries.map((item) => (
                <Badge key={item.industry_id} variant="default">
                  {item.industries?.name || "Unknown"}
                </Badge>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">No industries selected</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Skills */}
      <Card>
        <CardHeader>
          <CardTitle>Areas of Expertise</CardTitle>
          <CardDescription>Skills you can mentor on</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {userSkills && userSkills.length > 0 ? (
              userSkills.map((item) => (
                <Badge key={item.skill_id} variant="default">
                  {item.skills?.name || "Unknown"}
                </Badge>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">No skills selected</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Email Preferences */}
      <EmailPreferencesSection />
    </div>
  );
}
